/**
 * GlobalSearch — top-nav search across TDS parts, drawings, serial units,
 * NCRs, baselines and lot/serial numbers. Debounced, keyboard-navigable.
 */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, X, Package, FileText, Factory, ShieldAlert, Shield, GitMerge } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/tds-lifecycle/search`;

const KIND_ICON = {
  Part: Package, Drawing: FileText, Unit: Factory,
  NCR: ShieldAlert, Baseline: Shield, Lot: GitMerge,
};
const KIND_TONE = {
  Part: 'violet', Drawing: 'violet', Unit: 'amber',
  NCR: 'red', Baseline: 'sky', Lot: 'emerald',
};

const GlobalSearch = () => {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [cursor, setCursor] = useState(0);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  // Flatten groups → one results array for keyboard nav
  const flat = groups.flatMap((g) => g.results.map((r) => ({ ...r, group: g.group, kind: g.kind })));

  const runSearch = useCallback(async (term) => {
    if (!term || term.length < 2) { setGroups([]); return; }
    setLoading(true);
    try {
      const r = await fetch(`${API}?q=${encodeURIComponent(term)}&limit=6`);
      const d = await r.json();
      setGroups(d.groups || []);
      setCursor(0);
    } finally {
      setLoading(false);
    }
  }, []);

  // Debounced search
  useEffect(() => {
    const t = setTimeout(() => runSearch(q), 180);
    return () => clearTimeout(t);
  }, [q, runSearch]);

  // Global keyboard shortcut: Cmd/Ctrl+K
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
        setOpen(true);
      } else if (e.key === 'Escape') {
        setOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const pick = (r) => {
    setOpen(false); setQ('');
    if (r?.route) navigate(r.route);
  };

  const onKeyNav = (e) => {
    if (!flat.length) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setCursor((c) => (c + 1) % flat.length); }
    if (e.key === 'ArrowUp')   { e.preventDefault(); setCursor((c) => (c - 1 + flat.length) % flat.length); }
    if (e.key === 'Enter')     { e.preventDefault(); pick(flat[cursor]); }
  };

  return (
    <div className="relative" data-testid="global-search">
      <div className="flex items-center gap-1.5 bg-slate-800/50 border border-slate-700 rounded-md px-2 py-1 w-56">
        <Search className="w-3 h-3 text-slate-500" />
        <input
          ref={inputRef}
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          onKeyDown={onKeyNav}
          placeholder="Search parts, drawings, SN…"
          data-testid="global-search-input"
          className="flex-1 min-w-0 bg-transparent text-slate-200 text-[11px] outline-none placeholder:text-slate-500"
        />
        <kbd className="text-[9px] text-slate-500 font-mono border border-slate-700 rounded px-1">⌘K</kbd>
        {q && (
          <button onClick={() => { setQ(''); setGroups([]); inputRef.current?.focus(); }}
                  data-testid="global-search-clear"
                  className="text-slate-500 hover:text-slate-300">
            <X className="w-3 h-3" />
          </button>
        )}
      </div>

      {open && q.length >= 2 && (
        <div className="absolute right-0 mt-1 w-[380px] max-h-[70vh] overflow-auto bg-slate-900 border border-slate-700 rounded-lg shadow-2xl z-[60]"
             data-testid="global-search-results">
          {loading && <div className="p-3 text-xs text-slate-500">Searching…</div>}
          {!loading && !groups.length && (
            <div className="p-3 text-xs text-slate-500">No matches for "{q}".</div>
          )}
          {groups.map((g) => (
            <div key={g.group} className="border-b border-slate-800 last:border-0">
              <div className="px-3 py-1 text-[9px] uppercase tracking-wide text-slate-500 bg-slate-950/60 flex items-center gap-1">
                {React.createElement(KIND_ICON[g.kind] || Search, { className: 'w-3 h-3' })}
                {g.group} <span className="text-slate-600">· {g.results.length}</span>
              </div>
              {g.results.map((r) => {
                const idx = flat.findIndex((f) => f.id === r.id && f.group === g.group);
                const active = idx === cursor;
                const Icon = KIND_ICON[g.kind] || Search;
                const tone = KIND_TONE[g.kind] || 'violet';
                return (
                  <button
                    key={`${g.group}-${r.id}`}
                    onMouseDown={() => pick(r)}
                    onMouseEnter={() => setCursor(idx)}
                    data-testid={`gs-result-${r.id}`}
                    className={`w-full text-left px-3 py-1.5 flex items-start gap-2 transition ${
                      active ? 'bg-violet-500/10' : 'hover:bg-slate-800/60'
                    }`}
                  >
                    <Icon className={`w-3.5 h-3.5 text-${tone}-300 mt-0.5`} />
                    <div className="min-w-0 flex-1">
                      <div className="text-[11px] text-slate-200 truncate">{r.label}</div>
                      {r.meta && <div className="text-[10px] text-slate-500 truncate">{r.meta}</div>}
                    </div>
                  </button>
                );
              })}
            </div>
          ))}
          <div className="px-3 py-1 text-[9px] text-slate-600 bg-slate-950/60 flex items-center justify-between">
            <span>↑↓ navigate · ⏎ open · esc close</span>
            <span>Scope: TDS</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default GlobalSearch;
